import { c as _c } from "react/compiler-runtime";
import { ScoNode } from '@src/graphql-types';
import { useUniversalState } from '@src/hooks/useUniversalState';
import { useParams, usePathname } from 'next/navigation';
import { NextSeoProps } from 'next-seo/pages';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
export type ContentItem = ScoNode & {
  __typename?: string;
  type?: string;
  id?: string | number;
  slug?: string | number;
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  href?: string;
  featuredImageUrl?: string;
};
export type BreadcrumbItem = {
  slug?: string;
  title?: string;
  href?: string;
};
export type ActiveContentContextValue = {
  breadcrumbs?: BreadcrumbItem[];
  item?: ContentItem;
  otherItems?: ContentItem[];
  siteSection?: SiteSection;
  siteSectionParts?: string[];
  seoProps?: NextSeoProps;
  setBreadcrumbs?: (breadcrumbs: BreadcrumbItem[]) => void;
  setItem?: (item: ContentItem | any) => void;
  setOtherItems?: (items: ContentItem[]) => void;
  setSiteSection?: (section: SiteSection) => void;
  setSeoProps?: (seoProps: NextSeoProps) => void;
};
const noop = () => {};
const ActiveContentContext = createContext<ActiveContentContextValue>({
  breadcrumbs: [],
  otherItems: [],
  siteSectionParts: [],
  setBreadcrumbs: noop,
  setItem: noop,
  setOtherItems: noop,
  setSiteSection: noop,
  setSeoProps: noop
});
export const useActiveContentContext = () => {
  return useContext(ActiveContentContext);
};
export const ActiveContentContextProvider = props => {
  const $ = _c(27);
  const pathname = usePathname();
  const routeParams = useParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [breadcrumbs, setBreadcrumbs] = useUniversalState("ActiveContentContext.breadcrumbs", props.breadcrumbs, t0);
  const [item, setItem] = useUniversalState("ActiveContentContext.item", props.item, null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [otherItems, setOtherItems] = useUniversalState("ActiveContentContext.otherItems", props.otherItems, t1);
  const [siteSection, setSiteSection] = useUniversalState("ActiveContentContext.siteSection", props.siteSection, null);
  const [seoProps, setSeoProps] = useUniversalState("ActiveContentContext.seoProps", props.seoProps, null);
  let t2;
  if ($[2] !== item || $[3] !== routeParams || $[4] !== setItem) {
    t2 = () => {
      if (Object.keys(routeParams || {}).length == 0 && item) {
        setItem(null);
      }
    };
    $[2] = item;
    $[3] = routeParams;
    $[4] = setItem;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== pathname) {
    t3 = [pathname];
    $[6] = pathname;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== setSiteSection) {
    t4 = next => {
      setSiteSection(next);
    };
    $[8] = setSiteSection;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const setSiteSectionStable = t4;
  let t5;
  if ($[10] !== siteSection) {
    t5 = siteSection ? siteSection.split(".") : [];
    $[10] = siteSection;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== breadcrumbs || $[13] !== item || $[14] !== otherItems || $[15] !== seoProps || $[16] !== setBreadcrumbs || $[17] !== setItem || $[18] !== setOtherItems || $[19] !== setSeoProps || $[20] !== setSiteSectionStable || $[21] !== siteSection || $[22] !== t5) {
    t6 = {
      breadcrumbs,
      item,
      otherItems,
      seoProps,
      siteSection,
      siteSectionParts: t5,
      setBreadcrumbs,
      setItem,
      setOtherItems,
      setSeoProps,
      setSiteSection: setSiteSectionStable
    };
    $[12] = breadcrumbs;
    $[13] = item;
    $[14] = otherItems;
    $[15] = seoProps;
    $[16] = setBreadcrumbs;
    $[17] = setItem;
    $[18] = setOtherItems;
    $[19] = setSeoProps;
    $[20] = setSiteSectionStable;
    $[21] = siteSection;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const value = t6;
  let t7;
  if ($[24] !== props.children || $[25] !== value) {
    t7 = <ActiveContentContext.Provider value={value}>{props.children}</ActiveContentContext.Provider>;
    $[24] = props.children;
    $[25] = value;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};
export type SiteSection = 'about.culture' | 'about.jobs' | 'about.landing' | 'about.mission' | 'account.activity' | 'account.billing' | 'account.delete' | 'account.goals' | 'account.landing' | 'account.notifications' | 'account.profile' | 'account.reset-password' | 'account.security' | 'account.skills' | 'account.sprint' | 'account.startup' | 'account.startups-unlimited' | 'account.support' | 'account.tool-sessions' | 'admin' | 'advisors' | 'advisors.landing' | 'articles.landing' | 'articles.list' | 'articles.search' | 'articles.single' | 'lexicon.landing' | 'lexicon.list' | 'lexicon.search' | 'lexicon.single' | 'chat.landing' | 'comments.single' | 'community' | 'community.posts' | 'courses.landing' | 'courses.track' | 'courses.track.section' | 'courses.track.section.step' | 'courses.track.section.step.task' | 'education.landing' | 'events.group-meetings' | 'events.landing' | 'events.my-events' | 'events.office-hours' | 'events.office-hours.single' | 'events.past' | 'events.single' | 'events.workshops' | 'experts.landing' | 'experts.search' | 'experts.single' | 'founder-interviews.landing' | 'founder-interviews.list' | 'founder-interviews.search' | 'founder-interviews.single' | 'funding.landing' | 'home.landing' | 'investors.recommended' | 'investors.messages.list' | 'investors.messages.single' | 'investors.saved' | 'investors.search' | 'investors.single' | 'landing.advisor-sessions' | 'landing.funding-accelerator' | 'landing.investor-outreach' | 'landing.investor-matching' | 'landing.investor-match' | 'landing.funding-help' | 'landing.legacy-fg' | 'landing.30-day-plan' | 'landing.capital-compass' | 'landing.funding-readiness' | 'landing.pitch-deck-audit' | 'legal.guarantee' | 'legal.privacy' | 'investor-outreach.register' | 'investor-outreach.pitch-deck' | 'investor-outreach.payment' | 'investor-outreach.submitted' | 'investor-matching.register' | 'investor-matching.pitch-deck' | 'investor-matching.payment' | 'investor-matching.submitted' | 'legal.tos' | 'login' | 'logout' | 'funding-accelerator.rac' | 'funding-accelerator.payment' | 'members.search' | 'members.single' | 'members.matching' | 'notifications.list' | 'notifications.single' | 'onboarding.get-involved' | 'onboarding.goals' | 'onboarding.intro-call' | 'onboarding.profile' | 'onboarding.sprint' | 'onboarding.startup' | 'onboarding.welcome' | 'perks.landing' | 'perks.partners.landing' | 'perks.partners.single' | 'pitch-decks.landing' | 'playbooks.landing' | 'playbooks.single' | 'playbooks.single.phase' | 'podcast.landing' | 'podcast.search' | 'podcast.single' | 'posts.landing' | 'posts.search' | 'posts.single' | 'questions.landing' | 'questions.single' | 'requests.landing' | 'services.landing' | 'signup.payment' | 'signup.plans' | 'signup.profile' | 'signup.register' | 'sprints.landing' | 'sprints.track' | 'sprints.track.section' | 'sprints.track.section.step' | 'sprints.track.section.step.task' | 'tour.landing' | 'videos.landing' | 'videos.search' | 'videos.series' | 'videos.series.lesson' | 'workspace' | string;